import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { FirebaseContext } from 'common';

const C2 = {
  page: '#F4F2FE',
  card: '#FBFAFE',
  border: '#D7CFEB',
  sidebar: '#1A0F3B',
  muted: '#766293',
  violet: '#6411ED',
  bright: '#7628FF',
};

function readOobFromLocation() {
  if (typeof window === 'undefined') {
    return { mode: '', oobCode: '' };
  }
  const q = new URLSearchParams(window.location.search);
  let oobCode = q.get('oobCode') || '';
  // Algunos clientes convierten "+" en espacio.
  oobCode = oobCode.replace(/ /g, '+').trim();
  try {
    oobCode = decodeURIComponent(oobCode);
  } catch (_) {
    /* ya decodificado */
  }
  return {
    mode: q.get('mode') || '',
    oobCode,
  };
}

export default function AuthAction() {
  const firebaseCtx = useContext(FirebaseContext);
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [checking, setChecking] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [done, setDone] = useState(false);
  const [oobCode, setOobCode] = useState('');

  const resolveAuth = () => {
    if (firebaseCtx?.auth) return firebaseCtx.auth;
    if (typeof firebaseCtx?.authRef === 'function') return firebaseCtx.authRef();
    return null;
  };

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;

    const run = async () => {
      const { mode, oobCode: code } = readOobFromLocation();
      if (mode !== 'resetPassword' || !code) {
        if (!cancelled) {
          setError('Enlace inválido. Solicita de nuevo restablecer tu contraseña.');
          setChecking(false);
        }
        return;
      }
      setOobCode(code);

      const authInstance = resolveAuth();
      if (!authInstance) {
        attempts += 1;
        if (attempts < 20) {
          setTimeout(run, 150);
          return;
        }
        if (!cancelled) {
          setError('No se pudo inicializar autenticación. Recarga la página.');
          setChecking(false);
        }
        return;
      }

      try {
        const mail = await verifyPasswordResetCode(authInstance, code);
        if (!cancelled) {
          setEmail(mail || '');
          setError('');
          setErrorCode('');
          setChecking(false);
        }
      } catch (e) {
        if (!cancelled) {
          const codeName = e?.code || '';
          setErrorCode(codeName);
          setError(
            codeName === 'auth/expired-action-code' || codeName === 'auth/invalid-action-code'
              ? 'Este enlace caducó o ya se usó. Pide uno nuevo desde el login (usa solo el correo más reciente).'
              : `No se pudo validar el enlace (${codeName || 'error'}). Pide uno nuevo desde el login.`
          );
          setChecking(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseCtx]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorCode('');
    if (!password || password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    const authInstance = resolveAuth();
    if (!authInstance || !oobCode) {
      setError('Sesión de restablecimiento no lista. Recarga el enlace del correo.');
      return;
    }
    setSaving(true);
    try {
      await confirmPasswordReset(authInstance, oobCode, password);
      setDone(true);
    } catch (err) {
      setErrorCode(err?.code || '');
      setError(
        'No se pudo guardar la contraseña. El enlace puede haber caducado; solicita uno nuevo.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: C2.page,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 420,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          border: `1px solid ${C2.border}`,
          bgcolor: C2.card,
        }}
      >
        <Typography sx={{ fontWeight: 800, color: C2.sidebar, fontSize: 22, mb: 0.5 }}>
          Conecta2
        </Typography>
        <Typography sx={{ color: C2.muted, mb: 3, fontSize: 14 }}>
          Restablecer contraseña
        </Typography>

        {checking ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: C2.violet }} />
          </Box>
        ) : done ? (
          <>
            <Typography sx={{ color: C2.sidebar, mb: 2 }}>
              Tu contraseña se actualizó correctamente.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{
                background: `linear-gradient(90deg, ${C2.violet}, ${C2.bright})`,
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                py: 1.2,
              }}
            >
              Ir a iniciar sesión
            </Button>
          </>
        ) : error && !email ? (
          <>
            <Typography sx={{ color: '#B91C1C', mb: 1 }}>{error}</Typography>
            {errorCode ? (
              <Typography sx={{ color: C2.muted, fontSize: 12, mb: 2 }}>
                Código: {errorCode}
              </Typography>
            ) : null}
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate('/login')}
              sx={{ textTransform: 'none', borderColor: C2.violet, color: C2.violet }}
            >
              Volver al login
            </Button>
          </>
        ) : (
          <Box component="form" onSubmit={onSubmit}>
            {email ? (
              <Typography sx={{ color: C2.muted, fontSize: 13, mb: 2 }}>
                Cuenta: <strong style={{ color: C2.sidebar }}>{email}</strong>
              </Typography>
            ) : null}
            <TextField
              fullWidth
              type="password"
              label="Nueva contraseña"
              value={password}
              onChange={(ev) => setPassword(ev.target.value)}
              margin="dense"
              autoComplete="new-password"
            />
            <TextField
              fullWidth
              type="password"
              label="Confirmar contraseña"
              value={confirm}
              onChange={(ev) => setConfirm(ev.target.value)}
              margin="dense"
              autoComplete="new-password"
            />
            {error ? (
              <Typography sx={{ color: '#B91C1C', mt: 1, fontSize: 13 }}>{error}</Typography>
            ) : null}
            <Button
              fullWidth
              type="submit"
              disabled={saving}
              variant="contained"
              sx={{
                mt: 2.5,
                background: `linear-gradient(90deg, ${C2.violet}, ${C2.bright})`,
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                py: 1.2,
              }}
            >
              {saving ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Guardar contraseña'}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
